/**
 * B614 (a′) — the SHOW face's calendar (Figma 02 · Screens 554:2 @1440;
 * redline-storefront-show-calendar.json; NOTE 555:249). A month, Sunday
 * first: 165×134 r8 day cells 6 apart, a day with shows on #18181e with its
 * number in white, an empty one on #141419 with its number dim, TODAY ringed
 * in the accent and saying so; each show a 32-tall r6 accent@.22 tile with
 * its name (SS3-SB 12) over its times (SS3 10.5). A tile is the show's door.
 *
 * Under 600px the NOTE's phone rule holds — "a list by day (no 7-column grid
 * under 600)": the blanks, the column heads and the empty days fall away and
 * each remaining day names its weekday. One DOM for both, so assistive tech
 * reads one calendar. Every fact is the caller's; the card invents none.
 */
import type { ReactNode } from "react";

export interface ShowCalendarShow {
  readonly id: string;
  /** The headliner, as the poster names it. */
  readonly name: string;
  /** "7:00 · 9:45 · few left" — the caller's words. */
  readonly times: string;
  /** The tile's full name for assistive tech ("Cedric The Entertainer, Fri 2 — 7:00 · 9:45"). */
  readonly label: string;
}

export interface ShowCalendarDay {
  readonly day: number;
  /** "FRI" — drawn on the phone's list, where no column head stands above. */
  readonly weekday: string;
  readonly today: boolean;
  readonly shows: readonly ShowCalendarShow[];
}

const HEADS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as const;

export function ShowCalendar(props: {
  /** The month's name for assistive tech ("October 2026"). */
  readonly label: string;
  /** Blank cells before the 1st (its weekday, Sunday = 0). */
  readonly leading: number;
  readonly days: readonly ShowCalendarDay[];
  /** "Today" — the caller's word for the ringed cell. */
  readonly todayLabel: string;
  readonly onShow: (id: string) => void;
  readonly redline?: string;
}): ReactNode {
  return (
    <div className="t-showcal" role="group" aria-label={props.label} {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      <ol className="t-showcal-heads" aria-hidden="true">
        {HEADS.map((h) => (
          <li key={h} className="t-showcal-head">
            {h}
          </li>
        ))}
      </ol>
      <ol className="t-showcal-grid">
        {Array.from({ length: props.leading }, (_, i) => (
          <li key={`blank-${String(i)}`} className="t-showcal-blank" aria-hidden="true" />
        ))}
        {props.days.map((d) => (
          <li
            key={d.day}
            className={`t-showcal-day${d.today ? " t-showcal-day--today" : ""}${d.shows.length === 0 ? " t-showcal-day--empty" : ""}`}
            {...(d.today ? { "aria-current": "date" as const } : {})}
          >
            <p className="t-showcal-date">
              <span className="t-showcal-wd">{d.weekday}</span> {d.day}
              {d.today ? <span className="t-showcal-today">{props.todayLabel}</span> : null}
            </p>
            {d.shows.length > 0 ? (
              <ul className="t-showcal-shows">
                {d.shows.map((s) => (
                  <li key={s.id}>
                    <button type="button" className="t-showcal-show" aria-label={s.label} onClick={() => props.onShow(s.id)}>
                      <span className="t-showcal-name">{s.name}</span>
                      <span className="t-showcal-times">{s.times}</span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}
